import type { Lead } from '../../../../shared/types';
import { shell } from '../../app';
import { updateLead } from '../../data/store';
import type { Snapshot } from '../../data/store';
import { timeAgo } from '../../logic/dates';
import { groupByStage, lastVisitAt, searchLeads } from '../../logic/pipeline';
import { empty, leadTitle, row, section, stageSelect } from '../../ui/components';
import { h } from '../../ui/dom';
import type { Child } from '../../ui/dom';
import { STAGE_LABELS } from '../../ui/labels';

/** The row's stage select: a write the mirror refuses goes back to the stage it still has, and says so. */
const stageControl = (lead: Lead): HTMLSelectElement => {
  const sel = stageSelect(
    lead.stage,
    (stage) =>
      void updateLead(lead.id, { stage })
        .then(() => shell.toast(`${lead.business_name} → ${STAGE_LABELS[stage]}`))
        .catch((err: unknown) => {
          sel.value = lead.stage;
          shell.toast(`Could not save: ${err instanceof Error ? err.message : String(err)}`, {
            kind: 'error',
          });
        }),
    'stagesel--compact',
  );
  return sel;
};

/** The pipeline as stage sections of lead rows: the phone screen, and the desktop List mode (D65). */
export const pipelineSections = (snap: Snapshot, query: string): Child => {
  if (!snap.leads.length)
    return empty('No leads yet.', 'Tap + New, or log a visit to create one on the spot.');
  const last = lastVisitAt(snap.visits);
  const filtered = searchLeads(snap.leads, query);
  if (!filtered.length) return empty(`No lead matches “${query.trim()}”.`);
  const groups = groupByStage(filtered).filter((g) => g.leads.length > 0 || !query.trim());
  return groups.map((g) =>
    section(
      { title: `Current stage · ${STAGE_LABELS[g.stage]}`, count: g.leads.length },
      g.leads.length
        ? g.leads.map((lead) => {
            const lv = last.get(lead.id);
            return row({
              title: leadTitle(lead),
              meta: [
                lead.neighborhood ?? lead.address ?? '',
                lead.neighborhood || lead.address ? ' · ' : '',
                lv ? `visited ${timeAgo(lv)}` : 'never visited',
              ],
              href: `#/leads/${encodeURIComponent(lead.id)}`,
              trailing: stageControl(lead),
            });
          })
        : h('p', { class: 'section__empty muted' }, '—'),
    ),
  );
};
